// utils/chartDataTransform.js
import { formatDateDisplay } from './dateFormatters';
import { roundToTwo } from './costCalculations';

/**
 * Transform API trend data for Recharts
 */
export const transformTrendsForChart = (trendsData) => {
  if (!Array.isArray(trendsData) || trendsData.length === 0) {
    return [];
  }

  return trendsData.map(point => ({
    date: formatDateDisplay(point.date),
    cost: roundToTwo(point.cost || 0),
    inputCost: roundToTwo(point.inputCost || 0),
    outputCost: roundToTwo(point.outputCost || 0),
    requests: point.requests || 0,
    tokens: point.tokens || 0,
    inputTokens: point.inputTokens || 0,
    outputTokens: point.outputTokens || 0
  }));
};

/**
 * Transform users data for table display
 */
export const transformUsersForTable = (usersData) => {
  if (!Array.isArray(usersData) || usersData.length === 0) {
    return [];
  }

  return usersData.map(user => ({
    ...user,
    totalCost: roundToTwo(user.totalCost || 0),
    percentageOfTenantCost: roundToTwo(user.percentageOfTenantCost || 0),
    averageCostPerRequest: roundToTwo(user.averageCostPerRequest || 0)
  }));
};

/**
 * Transform conversations data for table display
 */
export const transformConversationsForTable = (conversations) => {
  if (!Array.isArray(conversations)) {
    return [];
  }

  return conversations.map(conversation => ({
    // ⭐ KEY FIX: Spread all fields first to preserve everything including timestamps
    ...conversation,
    
    // Then override/set specific fields as needed
    conversationTitle: conversation.conversationTitle || 'Untitled Conversation',
    
    // Transform requests while preserving ALL fields
    requests: conversation.requests?.map(request => ({
      ...request, // ⭐ Preserve all request fields including timestamp
      businessProcess: request.businessProcess || 'Untitled Process'
    })) || []
  }));
};

/**
 * Calculate summary statistics from overview data
 */
export const calculateSummaryStats = (overviewData) => {
  if (!overviewData) {
    return {
      totalCost: 0,
      inputCost: 0,
      outputCost: 0, 
      costLimit: 0,
      percentageUsed: 0,
      status: 'OK',
      totalRequests: 0,
      totalTokens: 0
    };
  }

  return {
    totalCost: roundToTwo(overviewData.totalCost || 0),
     inputCost: roundToTwo(overviewData.inputCost || 0),
    outputCost: roundToTwo(overviewData.outputCost || 0),
    costLimit: overviewData.costLimit || 0,
    percentageUsed: roundToTwo(overviewData.percentageUsed || 0),
    status: overviewData.status || 'OK',
    totalRequests: overviewData.totalRequests || 0,
    totalTokens: overviewData.totalTokens || 0,
    totalInputTokens: overviewData.totalInputTokens || 0,
    totalOutputTokens: overviewData.totalOutputTokens || 0,
    averageCostPerRequest: roundToTwo(overviewData.averageCostPerRequest || 0),
    projectedMonthlyCost: roundToTwo(overviewData.projectedMonthlyCost || 0),
    daysRemainingInPeriod: overviewData.daysRemainingInPeriod || 0
  };
};

/**
 * Prepare chart data with proper formatting
 */
export const prepareChartData = (data, dataKeys) => {
  if (!Array.isArray(data) || data.length === 0) {
    return [];
  }

  return data.map(item => {
    const formatted = { ...item };
    
    dataKeys.forEach(key => {
      if (typeof formatted[key] === 'number') {
        formatted[key] = roundToTwo(formatted[key]);
      }
    });
    
    return formatted;
  });
};